import type { ControllerSession } from '../controller';
import {
  type InputAckMessage,
  type InputAckableType,
  type HostMessage,
  type InputMessage,
  type VideoSettingsMessage,
  type RemoteCursorMessage,
  type RemoteCursorModeMessage,
  type SessionHeartbeatAckMessage,
} from '../../shared/datachannel';
import { type RemoteStreamSettings } from '../../shared/stream-profile';
import { POINTER_BUFFER_MAX, WHEEL_BUFFER_MAX } from './types';

const MAX_PENDING_INPUT_ACKS = 256;
let inputSequence = 0;

function inputAckableType(msg: InputMessage): InputAckableType | null {
  // 长按产生的重复 keydown 仍需实时发送，但不再为每一帧重复事件生成
  // 执行回执，避免长按方向键/退格时把控制通道和诊断表塞满。
  if (msg.type === 'keydown' && msg.repeat) return null;
  if (
    msg.type === 'mousedown' || msg.type === 'mouseup' || msg.type === 'click' ||
    msg.type === 'keydown' || msg.type === 'keyup'
  ) return msg.type;
  return null;
}

function createInputId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `i-${crypto.randomUUID()}`;
    }
  } catch {
    // 旧版 WebView 没有 randomUUID 时继续使用本地序列回退。
  }
  inputSequence = (inputSequence + 1) % 1_000_000;
  return `i-${Date.now().toString(36)}-${inputSequence.toString(36)}`;
}

export const controllerControl = {
  onControl(this: ControllerSession, raw: unknown, sourceChannel?: RTCDataChannel): void {
    // DataChannel 的旧事件可能在 close 后才从事件循环中回调；不能让它
    // 修改新会话的状态、确认回执或剪贴板。
    if (sourceChannel && this.dc !== sourceChannel) return;
    const parsed = this.controlMessageGuard.parse(raw);
    if (!parsed) return;
    this.markControlActivity();
    try {
      const msg = parsed as HostMessage | SessionHeartbeatAckMessage;
      if (msg.type === 'session-heartbeat-ack') {
        this.recordControlHeartbeatAck((msg as { ts?: number }).ts);
        return;
      }
      if (msg.type === 'input-ack') {
        this.recordInputAck(msg);
        return;
      }
      if (msg.type === 'clipboard' || msg.type === 'clipboard-ack') {
        this.onClipboardMessage(raw, sourceChannel || this.dc);
      } else if (msg.type === 'video-settings-ack') {
        this.cb.onVideoSettings?.(msg);
      } else if (msg.type === 'cursor-mode') {
        if (msg.mode === 'captured' || msg.mode === 'overlay') this.cb.onRemoteCursorMode?.(msg.mode);
      } else if (msg.type === 'input-permission') {
        this.remoteInputReady = msg.status === 'authorized';
        this.cb.onRemoteInputReady?.(
          this.remoteInputReady,
          msg.status === 'authorized' ? '' : '目标设备未授予辅助功能权限',
        );
        // 权限可能在控制通道打开后才返回；授权完成时再校准一次，避免
        // 用户在权限弹窗期间按住的修饰键与被控端状态不一致。
        if (this.remoteInputReady) this.sendInputState();
      }
    } catch {
      // ignore
    }
  },

  recordControlHeartbeatAck(this: ControllerSession, echoedAt?: number): void {
    const now = Date.now();
    this.controlHeartbeatAckAt = now;
    if (
      typeof echoedAt === 'number' &&
      Number.isFinite(echoedAt) &&
      echoedAt > now - 120_000 &&
      echoedAt <= now
    ) {
      // 心跳回显的是本端时间戳，不依赖两台设备的系统时钟同步。
      const sample = Math.max(0, now - echoedAt);
      this.controlRttMs = this.controlRttMs == null
        ? sample
        : Math.round(this.controlRttMs * 0.65 + sample * 0.35);
    }
  },

  recordInputAck(this: ControllerSession, message: InputAckMessage): void {
    if (
      !message || typeof message.id !== 'string' || message.id.length < 1 || message.id.length > 80 ||
      typeof message.inputType !== 'string' || typeof message.ok !== 'boolean'
    ) return;
    const pending = this.pendingInputAcks.get(message.id);
    if (!pending || pending.inputType !== message.inputType) return;
    this.pendingInputAcks.delete(message.id);
    const elapsed = Math.max(0, Date.now() - pending.sentAt);
    this.inputRttMs = this.inputRttMs == null
      ? elapsed
      : Math.round(this.inputRttMs * 0.65 + elapsed * 0.35);
    this.cb.onInputRtt?.(this.inputRttMs);
  },

  onCursorMessage(this: ControllerSession, raw: unknown, sourceChannel?: RTCDataChannel): void {
    if (sourceChannel && this.cursorDc !== sourceChannel) return;
    const parsed = this.cursorMessageGuard.parse(raw);
    if (!parsed) return;
    this.markConnectionActivity();
    try {
      const msg = parsed as unknown as RemoteCursorMessage | RemoteCursorModeMessage;
      if (msg.type === 'cursor-mode') {
        if (msg.mode === 'captured' || msg.mode === 'overlay') this.cb.onRemoteCursorMode?.(msg.mode);
        return;
      }
      if (msg.type !== 'cursor' || typeof msg.visible !== 'boolean') return;
      if (msg.visible && (typeof msg.data !== 'string' || !msg.data.startsWith('data:image/'))) return;
      this.cb.onRemoteCursor?.(msg);
    } catch {
      // 光标状态是可丢弃的增强消息，损坏时等待下一帧状态即可。
    }
  },

  sendInput(this: ControllerSession, msg: InputMessage): void {
    // 仅观看会话仍允许调整远端画面质量，但任何输入、剪贴板和文件操作
    // 都必须在协议层被拦截，不能只依赖界面隐藏按钮。
    if (this.accessMode === 'view' && msg.type !== 'video-settings') return;
    // 在发送前更新本端的“当前按住状态”。即使连接此刻短暂不可用，
    // 用户松键/松开鼠标后，下一次重连校准也会得到正确的最终状态。
    if (msg.type === 'keydown') this.heldInputKeys.add(msg.key);
    else if (msg.type === 'keyup') this.heldInputKeys.delete(msg.key);
    else if (msg.type === 'mousedown') this.heldInputButtons.add(msg.button);
    else if (msg.type === 'mouseup') this.heldInputButtons.delete(msg.button);
    // 剪贴板不能和键盘事件共享同一条有序通道：大文本会造成背压，且
    // 原来的单向 send 没有确认，网络瞬时抖动时用户只能无感知地丢失粘贴。
    if (msg.type === 'clipboard') {
      void this.sendClipboardText(msg.text, msg.html, msg.rtf);
      return;
    }
    if (msg.type === 'clipboard-read') {
      this.requestRemoteClipboard();
      return;
    }
    if (msg.type === 'video-settings') {
      this.sendVideoSettings(msg);
      return;
    }

    const isMouseMove = msg.type === 'mousemove';
    const isWheel = msg.type === 'wheel';
    const pointer = isMouseMove ? this.pointerDc : null;
    const wheel = isWheel ? this.wheelDc : null;
    const channel = isWheel
      ? wheel?.readyState === 'open'
        ? wheel
        : this.pointerDc?.readyState === 'open'
          ? this.pointerDc
          : this.dc
      : pointer?.readyState === 'open'
        ? pointer
        : this.dc;
    if (
      !channel ||
      channel.readyState !== 'open' ||
      (!isMouseMove && !isWheel && !this.controlReady) ||
      this.remoteInputReady === false
    ) {
      if (!isMouseMove && !isWheel) {
        const now = Date.now();
        if (now - this.lastControlWarningAt > 1000) {
          this.lastControlWarningAt = now;
          this.cb.onStatus(
            this.remoteInputReady === false
              ? '目标设备未授权输入权限，鼠标键盘不会生效'
              : '连接尚未准备好，请稍候再操作',
            'error',
          );
        }
      }
      return;
    }
    // 鼠标移动允许丢弃过期坐标，避免网络拥塞时把控制端拖成“输入回放”。
    if (isMouseMove && channel.bufferedAmount > POINTER_BUFFER_MAX) return;
    // 滚轮使用独立通道，并保留一个较小的上限：网络严重拥塞时丢弃过期
    // 滚动比把旧滚动动作排队到几秒后再执行更符合用户预期。
    if (isWheel && channel.bufferedAmount > WHEEL_BUFFER_MAX) return;
    const ackType = inputAckableType(msg);
    let inputId: string | null = null;
    let outbound: InputMessage = msg;
    if (ackType) {
      inputId = createInputId();
      if (this.pendingInputAcks.size >= MAX_PENDING_INPUT_ACKS) {
        const oldest = this.pendingInputAcks.keys().next().value;
        if (typeof oldest === 'string') this.pendingInputAcks.delete(oldest);
      }
      this.pendingInputAcks.set(inputId, { sentAt: Date.now(), inputType: ackType });
      outbound = { ...msg, id: inputId } as InputMessage;
    }
    try {
      channel.send(JSON.stringify(outbound));
    } catch {
      if (inputId) this.pendingInputAcks.delete(inputId);
      if (!isMouseMove && !isWheel) {
        this.cb.onStatus('连接暂时不可用，请重试', 'error');
      }
    }
  },

  sendInputState(this: ControllerSession): void {
    if (this.accessMode === 'view') return;
    const channel = this.dc;
    if (!channel || channel.readyState !== 'open' || !this.controlReady) return;
    try {
      channel.send(JSON.stringify({
        type: 'input-state',
        // 正常用户不会同时按下这么多键；上限只用于防止异常页面状态
        // 把状态校准消息放大成无界输入。
        keys: Array.from(this.heldInputKeys).slice(0, 64),
        buttons: Array.from(this.heldInputButtons).slice(0, 3),
      }));
    } catch {
      // 控制通道恢复后会再次发送；校准失败不影响实时输入路径。
    }
  },

  sendVideoSettings(this: ControllerSession, settings: RemoteStreamSettings): boolean {
    const channel = this.dc;
    // 画面偏好和输入权限是两条独立链路。目标端尚未授权辅助功能时，
    // 仍应允许用户调整画面，避免“不能控制”连带变成“不能看清”。
    if (!channel || channel.readyState !== 'open' || !this.controlReady) {
      this.cb.onStatus('连接尚未准备好，暂时无法调整画面', 'error');
      return false;
    }
    const message: VideoSettingsMessage = { type: 'video-settings', ...settings };
    try {
      channel.send(JSON.stringify(message));
      return true;
    } catch {
      this.cb.onStatus('画面设置发送失败，请重试', 'error');
      return false;
    }
  },

  setVideoEnabled(this: ControllerSession, enabled: boolean): void {
    this.videoEnabled = enabled;
    this.pc?.getReceivers().forEach((receiver) => {
      if (receiver.track?.kind === 'video') receiver.track.enabled = enabled;
    });
  },
};
